<template>
  <div>
    <form-item-check-box
      label="宛先(TO)"
      form-label-class-name="pega-form-label-important"
      :items="recipient.to"
    />
    <form-item-check-box
      label="宛先(CC)"
      form-label-class-name="pega-form-label-important"
      :items="recipient.cc"
      @delete="deleteNotificationAccount"
    >
      <template #footer>
        <small
          v-if="isCompanyStaffAccess"
          data-test="cc-message"
        >※アカウントを発行すると宛先(CC)に追加されます。アカウント管理は、設定管理者のみ行えます。</small>
        <template v-else>
          <input
            v-model="name"
            type="text"
            class="boss-input pega-height-35"
            data-test="input-name"
            size="30"
            placeholder="名前"
          >
          <input
            v-model="email"
            type="text"
            class="boss-input pega-height-35"
            data-test="input-email"
            size="60"
            placeholder="メールアドレス"
          >
          <div class="pega-mt-05">
            <button
              :disabled="isAddButtonDisabled"
              class="boss-button pega-button boss-button-center"
              data-test="add-account"
              @click="registerNotificationAccount"
            >
              追加
            </button>
          </div>
        </template>
      </template>
    </form-item-check-box>
    <form-item-text-area
      v-model="messageBody"
      form-label-class-name="pega-form-label-important"
      :class-name="isOverLengthBody ? 'boss-send-textarea is-invalid-input': 'boss-send-textarea'"
      rows
    >
      <template #label>
        メッセージ<br>
        <small>（2,000文字以内）</small>
      </template>
      <template #error>
        <validation-label
          v-if="isOverLengthBody"
          label="本文は2000文字以内で入力してください"
        />
      </template>
    </form-item-text-area>

    <form-button-area>
      <form-confirm-button
        :is-disabled="isSendButtonDisabled"
        button-class="pega-confirm-important"
        submit-label="メッセージを送信する"
        @submit-event="submit"
      />
    </form-button-area>
  </div>
</template>

<script>
import FormItemCheckBox from '@/components/atoms/FormItemCheckBox'
import FormItemTextArea from '@/components/atoms/FormItemTextArea'
import ValidationLabel from '@/components/atoms/ValidationLabel'
import FormButtonArea from '@/components/atoms/FormButtonArea'
import FormConfirmButton from '@/components/atoms/FormConfirmButton'
import bossSCommon from '@/lib/bossSCommon'
import Indicator from '@/lib/indicator'

export default {
  components: {
    FormItemCheckBox,
    FormItemTextArea,
    ValidationLabel,
    FormButtonArea,
    FormConfirmButton
  },
  mixins: [bossSCommon],
  data () {
    return {
      name: '',
      email: '',
      messageBody: '',
      recipient: { cc: [], to: [] }
    }
  },
  computed: {
    notificationApiPath () { return `/preview/prepublish/companies/${this.companyId}/notification_accounts` },
    isEmptyName () {
      return this.name.trim().length === 0
    },
    isEmptyEmail () {
      return this.email.trim().length === 0
    },
    isAddButtonDisabled () {
      return this.isEmptyName || this.isEmptyEmail
    },
    isEmptyBody () {
      return this.messageBody.trim().length === 0
    },
    isOverLengthBody () {
      return this.messageBody.length > 2000
    },
    checkedTo () {
      return this.recipient.to.filter(sender => sender.isChecked)
    },
    checkedCc () {
      return this.recipient.cc.filter(sender => sender.isChecked)
    },
    isNoSendUser () {
      return !this.checkedTo.length && !this.checkedCc.length
    },
    isSendButtonDisabled () {
      return this.isEmptyBody || this.isOverLengthBody || this.isNoSendUser
    }
  },
  created () {
    this.fetchRecipients()
  },
  methods: {
    fetchRecipients () {
      this.$http.secured.get(`${this.apiPath}/recipients`).then((response) => {
        this.$set(this, 'recipient', response.data)
        this.recipient.to.forEach(recipient => {
          this.$set(recipient, 'label', `${recipient.name} (${recipient.email})`)
        })
        this.recipient.cc.forEach(recipient => {
          this.$set(recipient, 'label', `${recipient.name} (${recipient.email})`)
          this.$set(recipient, 'isShowTrash', !this.isCompanyStaffAccess && this.isNotificationUser(recipient.categoryId))
        })
      })
    },
    isNotificationUser (categoryId) {
      // CCリストでNotificationUser(categoryId=3)のみボタンを表示する
      return categoryId === 3
    },
    registerNotificationAccount () {
      const params = {
        name: this.name,
        email: this.email
      }
      Indicator.emitShow()
      this.$http.secured.post(this.notificationApiPath, params).then(() => {
        this.$toasted.global.success({ message: 'CCに登録完了しました。' })
        this.fetchRecipients()
        this.$set(this, 'name', '')
        this.$set(this, 'email', '')
      }).catch(() => { /* NOP */ })
        .finally(() => {
          Indicator.emitHide()
        })
    },
    // 送信ボタン押下時
    submit () {
      // checkedされたID取得
      const messageSend = this.checkedTo.concat(this.checkedCc).map(recipient => ({
        categoryId: recipient.categoryId,
        id: recipient.id
      }))
      // 送信先、新規ユーザー・メッセージ追加
      const requestForm = {
        body: this.messageBody,
        sendTo: messageSend
      }
      Indicator.emitShow()
      this.$http.secured.post(`${this.apiPath}/messages`, requestForm).then(() => {
        this.$toasted.global.success({ message: 'メッセージを送信しました。' })
        this.$emit('fetchMessages')
        this.fetchRecipients()
        this.$set(this, 'messageBody', '')
      }).catch(() => { /* NOP */ })
        .finally(() => {
          Indicator.emitHide()
        })
    },
    deleteNotificationAccount (id) {
      this.$dialog.confirm('メッセージの履歴からも削除されます。本当に削除しますか？')
        .then(() => {
          this.$http.secured.delete(`${this.notificationApiPath}/${id}`).then(() => {
            this.$toasted.global.success({ message: '通知ユーザーを削除しました。' })
            this.fetchRecipients()
          })
        }) // いいえを押下した時（catch句を消すとコンソールに警告が出るから消さないでね！！）
        .catch(() => {})
    }
  }
}
</script>
